// Draws a PDF's pages as images so the page itself can print them.
//
// Chrome (153+) treats a PDF shown inside a frame as another origin: the page
// may not call print() on it, so a label loaded that way could only be
// downloaded (Volkan 2026-10-27, checked in his Chrome). Pages drawn here are
// plain images that belong to the page, and print from a same-origin frame.
// pdf.js is bundled (the admin CSP allows only 'self' scripts) and loaded only
// when something is printed.

/** Thermal label printers run at 203 or 300 DPI; 300 keeps barcodes crisp on both. */
const PRINT_DPI = 300
const PDF_POINTS_PER_INCH = 72

export type RenderedPage = { image: Blob; widthIn: number; heightIn: number }

function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('page image failed'))), 'image/png')
  })
}

export async function renderPdfPages(pdf: Blob): Promise<RenderedPage[]> {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    // Bundled as a .js worker: the serving nginx has no type for .mjs, and
    // Chrome refuses a module worker served as application/octet-stream.
    import('pdfjs-dist/build/pdf.worker.min.mjs?worker'),
  ])
  // One worker for the session; later prints reuse it.
  pdfjs.GlobalWorkerOptions.workerPort ??= new worker.default()
  const doc = await pdfjs.getDocument({
    data: new Uint8Array(await pdf.arrayBuffer()),
    // The admin CSP forbids eval; pdf.js has a slower path without it.
    isEvalSupported: false,
  }).promise
  try {
    const pages: RenderedPage[] = []
    // One canvas, reused: a 4x6 page at 300 DPI is ~9 MB of pixels.
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) throw new Error('no canvas')
    for (let n = 1; n <= doc.numPages; n += 1) {
      const page = await doc.getPage(n)
      const size = page.getViewport({ scale: 1 })
      const viewport = page.getViewport({ scale: PRINT_DPI / PDF_POINTS_PER_INCH })
      // Rounded, not ceiled: 432 pt × 300/72 lands a hair over 1800 px.
      canvas.width = Math.round(viewport.width)
      canvas.height = Math.round(viewport.height)
      // Labels go to thermal printers: a white page, never a transparent one.
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: context, viewport }).promise
      pages.push({
        image: await canvasToPng(canvas),
        widthIn: size.width / PDF_POINTS_PER_INCH,
        heightIn: size.height / PDF_POINTS_PER_INCH,
      })
      page.cleanup()
    }
    return pages
  } finally {
    await doc.destroy()
  }
}
